import React, { useEffect, useRef } from "react";

const BouncingBallsBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const COLORS = ["#FF3E30", "#F7B529", "#179C52", "#176BEF","#666"];

    let balls = [];
    const BALL_COUNT = 12;

    function createBalls(count) {
      const arr = [];
      let maxAttempts = 1000;
      for (let i = 0; i < count; i++) {
        let attempts = 0;
        let newBall;
        let overlapping;

        do {
          // Generate random ball
          newBall = {
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height,
            r: 60 + Math.random() * 110,
            dx: (Math.random() - 0.5) * 0.5,
            dy: (Math.random() - 0.5) * 0.5,
            color: COLORS[Math.floor(Math.random() * COLORS.length)],
          };

          // Check overlap with all existing balls
          overlapping = false;
          for (let j = 0; j < arr.length; j++) {
            const existingBall = arr[j];
            const dx = newBall.x - existingBall.x;
            const dy = newBall.y - existingBall.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            // If they overlap
            if (distance < newBall.r + existingBall.r) {
              overlapping = true;
              break;
            }
          }

          attempts++;
          // Stop if we've tried too many times 
          // (in case BALL_COUNT is very large or canvas is too small)
        } while (overlapping && attempts < maxAttempts);

        arr.push(newBall);
      }
      return arr;
    }

    function resolveCollision(ballA, ballB) {
      const dx = ballB.x - ballA.x;
      const dy = ballB.y - ballA.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Avoid zero distance to prevent NaNs
      if (distance === 0) return;

      // Normalized direction vector from ballA to ballB
      const nx = dx / distance;
      const ny = dy / distance;

      // Project velocities along that normal
      const pA = ballA.dx * nx + ballA.dy * ny;
      const pB = ballB.dx * nx + ballB.dy * ny;

      // Swap the projected velocities (perfectly elastic collision)
      const temp = pA;
      const newPA = pB;
      const newPB = temp;

      // Update the velocities
      ballA.dx += (newPA - pA) * nx;
      ballA.dy += (newPA - pA) * ny;
      ballB.dx += (newPB - pB) * nx;
      ballB.dy += (newPB - pB) * ny;
    }

    function animate() {
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Move each ball
      for (let i = 0; i < balls.length; i++) {
        const ball = balls[i];

        ball.x += ball.dx;
        ball.y += ball.dy;

        // Bounce off walls
        if (ball.x + ball.r > canvas.width || ball.x - ball.r < 0) {
          ball.dx = -ball.dx;
        }
        if (ball.y + ball.r > canvas.height || ball.y - ball.r < 0) {
          ball.dy = -ball.dy;
        }
      }

      // Detect and resolve collisions between balls
      for (let i = 0; i < balls.length; i++) {
        for (let j = i + 1; j < balls.length; j++) {
          const ballA = balls[i];
          const ballB = balls[j];

          const dx = ballB.x - ballA.x;
          const dy = ballB.y - ballA.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // If circles overlap
          if (distance < ballA.r + ballB.r) {
            resolveCollision(ballA, ballB);
          }
        }
      }

      // Draw each ball
      for (let i = 0; i < balls.length; i++) {
        const ball = balls[i];
        ctx.beginPath();
        ctx.arc(ball.x, ball.y, ball.r, 0, Math.PI * 2);
        ctx.fillStyle = ball.color;
        ctx.fill();
        ctx.closePath();
      }

      requestAnimationFrame(animate);
    }

    function handleResize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      balls = createBalls(BALL_COUNT);
    }

    window.addEventListener("resize", handleResize);
    handleResize();
    animate();

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: -1,
      }}
    />
  );
};

export default BouncingBallsBackground;
